/**
 * Global date formatting utilities
 * Provides consistent date formatting across the application
 */

/**
 * Formats a date string or Date object to dd/mm/yyyy format
 * @param dateString - The date string or Date object to format
 * @returns Formatted date string in dd/mm/yyyy format
 */
export const formatDate = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Formats a date string or Date object to Vietnamese locale format (dd/mm/yyyy)
 * Uses toLocaleDateString with vi-VN locale
 * @param dateString - The date string or Date object to format
 * @returns Formatted date string
 */
export const formatDateVietnamese = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return date.toLocaleDateString('vi-VN', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
};

/**
 * Formats a date string or Date object to yyyy-mm-dd format (ISO date format)
 * Useful for HTML date inputs
 * @param dateString - The date string or Date object to format
 * @returns Formatted date string in yyyy-mm-dd format
 */
export const formatDateISO = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return date.toISOString().split('T')[0];
};

/**
 * Parses a date string in various formats and returns a Date object
 * @param dateString - The date string to parse
 * @returns Date object or null if invalid
 */
export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};
